import {
  ApolloClient,
  NormalizedCacheObject,
  createHttpLink,
  from,
  ApolloLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { createClient } from 'graphql-ws';
import { APP_CONFIG } from '../config';
import { AuthErrorCodes } from '@oolio-group/domain';

if (import.meta.env.DEV) {
  import('@apollo/client/dev').then(
    ({ loadDevMessages, loadErrorMessages }) => {
      loadDevMessages();
      loadErrorMessages();
    },
  );
}

export interface SessionServiceType {
  getHeaders(): Promise<Record<string, string>>;
  getWSPayload(): Promise<Record<string, string>>;
  clearSession(): Promise<void>;
}

interface ApolloClientConfig {
  // Fixed like API key
  headers?: Record<string, string>;
  links?: ApolloLink[];
  graphqlUri: string;
  // from state
  getCustomHeaders?: () =>
    | Record<string, string>
    | Promise<Record<string, string>>;
  // From service
  sessionSvc: SessionServiceType;
}

export const createApolloClient = (
  config: ApolloClientConfig,
): ApolloClient<NormalizedCacheObject> => {
  const customHeaders = config.headers || {};

  const tokenExpiredErrorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors) {
      if (
        graphQLErrors.some(
          gqlError =>
            gqlError?.extensions?.code === AuthErrorCodes.UNAUTHENTICATED,
        )
      ) {
        void config.sessionSvc.clearSession();
      }
    }
  });
  const customLinks = [tokenExpiredErrorLink, ...(config.links ?? [])];

  const graphqlUri = config.graphqlUri;

  const cache = new InMemoryCache({
    // getApolloCache cahce policies migth not be good fit fot orb
    typePolicies: {},
  });

  // Create HTTP link
  const httpLink = createHttpLink({
    uri: graphqlUri,
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: APP_CONFIG.SUBSCRIPTION_URL || 'ws://localhost:4000/graphql',
      connectionParams: async () => {
        const payload = await config.sessionSvc.getWSPayload();
        return payload;
      },
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    httpLink,
  );

  // Create auth link to add token to headers
  const authLink = setContext(async (_, { headers }) => {
    // Start with incoming headers and static custom headers
    let combinedHeaders = { ...headers, ...customHeaders };

    // Get dynamic custom headers if provided
    const dynamicHeaders = await config.sessionSvc.getHeaders();
    combinedHeaders = { ...combinedHeaders, ...dynamicHeaders };

    return {
      headers: combinedHeaders,
    };
  });

  // Create the link chain: custom links (if any) -> auth link -> split link
  const linkChain =
    customLinks.length > 0
      ? from([...customLinks, authLink, splitLink])
      : authLink.concat(splitLink);

  return new ApolloClient({
    link: linkChain,
    // This uses the configured cache,
    // Sometimes this might not works for all apps.
    // Need to see how this is working for ORB
    cache,
  });
};
